import React, {useCallback, useContext, useEffect} from 'react';


import {DISCARD_CHANGES_SUCCESS_MESSAGE, SAVE_CHANGES_SUCCESS_MESSAGE} from 'src/constants';
import {useConfirmationChunk} from 'src/contexts/confirmation-context';
import {MessageContext} from 'src/contexts/message-context';
import {ValidationContext} from 'src/contexts/validation-context';
import {ValidationContextProvider} from 'src/contexts/validation-context/validation-context-provider';
import {useEffectWithDeps} from 'src/hooks/useEffectWithDeps';
import {BASIC_VALIDATION_FAILED} from 'src/modules/create-account/constants';
import {CrmType as CrmTypes} from 'src/openapi/admin/models/CrmType';

import {FlatButton} from '@pb/kit-component-button';
import Icon, {Icons} from '@pb/kit-component-icon';
import {SlideBoxButton} from '@pb/kit-component-slide-box';

import {CrmRow} from '../../components/crm-row';
import {useCrmLimits} from '../../useCrmLimits';
import {useEnabledCrmModules} from '../../useEnabledCrmModules';

import {DealCloudSection} from './deal-cloud-section';
import {useDealCloudCredentials} from './useDealCloudCredentials';

import type {CrmComponentProps} from '../../license-crm-tab';

const DealCloudRow = (props: CrmComponentProps) => {
    const {
        licenseId,
        accountId,

        currentCrm,
        runSaveChanges,
        runDiscardChanges,

        onChangeActiveCrm,
        onChangesSaved,
        onChangesDiscarded,
        onActionDisabled,
        editDisabled,
    } = props;

    const {setErrorMessage, setSuccessMessage} = useContext(MessageContext);
    const {clearErrors, validationErrors} = useContext(ValidationContext);

    const {isSomeChunkChanged, isCurrentChunkChanged, setIsChunkChanged, onShowModal, proceed} = useConfirmationChunk({
        chunkName: 'deal-cloud-crm',
        onProceed: () => {
            onChangesSaved();
            setSuccessMessage(SAVE_CHANGES_SUCCESS_MESSAGE);
        },
    });

    const {
        isLoading: isModuleLoading,
        loadingError: moduleLoadingError,
        enabled,
        saveCrmModuleState,
    } = useEnabledCrmModules({licenseId, crmType: CrmTypes.DEALCLOUDCRM});

    const {
        isLoading: isCredentialsLoading,
        loadingError: credentialsLoadingError,
        isChanged: isCredentialsChanged,
        data: credentials,
        setData: setCredentials,
        saveChanges: saveCredentials,
        discardChanges: discardCredentials,
        loadData: loadCredentials,
    } = useDealCloudCredentials({accountId});

    const {
        isLoading: isLimitsLoading,
        loadingError: limitsLoadingError,
        isChanged: isCrmLimitsChanged,

        data: crmLimits,
        setData: setCrmLimits,

        saveChanges: saveCrmLimits,
        discardChanges: discardCrmLimits,
        loadData: loadCrmLimits,
    } = useCrmLimits({licenseId, crmType: CrmTypes.DEALCLOUDCRM, accountId});

    const saveChanges = useCallback(async () => {
        const calls = [];

        if (validationErrors.length) {
            setErrorMessage(BASIC_VALIDATION_FAILED);
            onChangesSaved();
            return;
        }

        if (isCredentialsChanged) {
            calls.push(await saveCredentials());
        }

        if (isCrmLimitsChanged) {
            calls.push(await saveCrmLimits());
        }

        const errors = calls.filter(Boolean);

        if (errors.length) {
            onChangesSaved();
            setErrorMessage(errors.join(' '));
        } else {
            proceed();
        }
    }, [
        validationErrors,
        isCrmLimitsChanged,
        saveCrmLimits,
        isCredentialsChanged,
        saveCredentials,
        onChangesSaved,
        proceed,
        setErrorMessage,
    ]);

    const discardChanges = useCallback(() => {
        discardCrmLimits();
        discardCredentials();

        clearErrors();
        onChangesDiscarded();
        setSuccessMessage(DISCARD_CHANGES_SUCCESS_MESSAGE);
    }, [discardCrmLimits, discardCredentials, clearErrors, onChangesDiscarded, setSuccessMessage]);

    const changeActiveCrm = useCallback(() => {
        if (isCurrentChunkChanged) {
            onShowModal(() => {
                discardChanges();
                onChangeActiveCrm(currentCrm !== CrmTypes.DEALCLOUDCRM ? CrmTypes.DEALCLOUDCRM : null);
            });
        } else {
            onChangeActiveCrm(currentCrm !== CrmTypes.DEALCLOUDCRM ? CrmTypes.DEALCLOUDCRM : null);
        }
    }, [isCurrentChunkChanged, discardChanges, currentCrm, onChangeActiveCrm, onShowModal]);

    const toggleCrmModules = useCallback<React.MouseEventHandler<HTMLDivElement>>(event => {
        if (enabled && isCurrentChunkChanged) {
            onShowModal(() => {
                discardChanges();
                onChangeActiveCrm(null);
                saveCrmModuleState(false);
            });
        } else if (enabled) {
            onChangeActiveCrm(null);
            saveCrmModuleState(false);
        } else {
            saveCrmModuleState(true);
        }
        event.preventDefault();
    }, [isCurrentChunkChanged, discardChanges, enabled, onChangeActiveCrm, saveCrmModuleState, onShowModal]);

    useEffectWithDeps((prev, current) => {
        if (currentCrm === CrmTypes.DEALCLOUDCRM && prev.currentCrm !== current.currentCrm && loadCredentials) {
            loadCredentials();
        }
    }, {currentCrm, loadCredentials});

    useEffect(() => {
        if (licenseId && loadCrmLimits) {
            loadCrmLimits();
        }
    }, [licenseId, loadCrmLimits]);

    useEffect(() => {
        setIsChunkChanged([
            isCrmLimitsChanged,
            isCredentialsChanged,
        ].some(Boolean));
    }, [
        setIsChunkChanged,
        isCrmLimitsChanged,
        isCredentialsChanged,
    ]);

    useEffect(() => {
        if (runSaveChanges && isCurrentChunkChanged) {
            saveChanges();
        }
    }, [runSaveChanges, saveChanges, isCurrentChunkChanged]);

    useEffect(() => {
        if (runDiscardChanges && isCurrentChunkChanged) {
            discardChanges();
        }
    }, [runDiscardChanges, discardChanges, isCurrentChunkChanged]);

    const isRowLoading = [
        isModuleLoading,
        isLimitsLoading,
    ].some(Boolean);

    const isLoading = isCredentialsLoading;

    const loadingError = [
        moduleLoadingError,
        credentialsLoadingError,
        limitsLoadingError,
    ].find(Boolean) || null;

    useEffect(() => {
        onActionDisabled(!isCurrentChunkChanged || isRowLoading || isLoading || !!loadingError);
    }, [isCurrentChunkChanged, isRowLoading, isLoading, loadingError, onActionDisabled]);

    return (
        <CrmRow
            isLoading={isRowLoading}
            currentCrm={currentCrm}
            loadingError={loadingError}
            editDisabled={editDisabled}
            crmType={CrmTypes.DEALCLOUDCRM}
            crmName="DealCloud"
            enabled={enabled}
            onToggleCrmModules={toggleCrmModules}
            crmLimits={crmLimits}
            connectionStatus={<div/>}
            slideBoxButton={(
                <SlideBoxButton onClick={changeActiveCrm}>
                    {() => (
                        <div className="pdy-xs">
                            <FlatButton
                                startIcon={<Icon type={Icons.PENCIL}/>}
                                onClick={changeActiveCrm}
                                disabled={
                                    isSomeChunkChanged(chunk => chunk.includes('crm')) &&
                                    currentCrm !== CrmTypes.DEALCLOUDCRM
                                }
                                endIcon={(
                                    <Icon type={currentCrm === CrmTypes.DEALCLOUDCRM
                                        ? Icons.ANGLE_UP
                                        : Icons.ANGLE_DOWN}
                                    />
                                )}>
                                Configure settings
                            </FlatButton>
                        </div>
                    )}
                </SlideBoxButton>
            )}>
            <DealCloudSection
                isLoading={isLoading}
                disabled={!enabled}
                readonly={editDisabled}
                limits={crmLimits}
                credentials={credentials}
                onChangeLimits={setCrmLimits}
                onChangeCredentials={setCredentials}
            />
        </CrmRow>
    );
};

export const ValidatedDealCLoudRow = (props: CrmComponentProps) => (
    <ValidationContextProvider>
        <DealCloudRow {...props}/>
    </ValidationContextProvider>
);
